// Shared field definitions and validation for the voyage specification tabs
// (wizard Options step, also embedded by the RNW analyser panel).

import { nowUtc } from "@/utils/appNow"
import { resolveWeatherLimits } from "@/features/vessel/vesselMeta.js"

// Single definition of what makes a departure time acceptable, shared by the
// Plan-step warning and the compute-button gate so they never drift.
export function isDepartureOutdated(time) {
  return (
    !!time?.isValid?.() && time.isBefore(nowUtc().startOf("minute"))
  )
}

export function isDepartureValid(time) {
  return !!time?.isValid?.() && !isDepartureOutdated(time)
}

// Vessel master-data fields — there is no default vessel, so these start
// empty in the planner settings and are filled from the selected vessel's
// meta at compute time.
export const VESSEL_META_FIELDS = [
  "imo",
  "lengthM",
  "beamM",
  "maxDraftM",
  "grossTonnage",
  "shiptype",
  "nationality",
  "mmsi",
  "fleetname",
  "owner",
  "charterer",
]

const hasValue = (v) => v !== null && v !== undefined && v !== ""

// Request-time merge: the vessel's real master data wins over whatever is in
// the settings (the settings copy may be stale or empty). Done at the request
// boundary — not in state — so it never triggers draft invalidation.
export function mergeVesselMetaIntoSettings(settings, vesselMeta) {
  const merged = { ...settings, ...resolveWeatherLimits(settings, vesselMeta) }
  if (!vesselMeta) return merged
  VESSEL_META_FIELDS.forEach((field) => {
    if (hasValue(vesselMeta[field])) merged[field] = vesselMeta[field]
  })
  return merged
}

// The dimensions the RNW restriction checks need — computing without them
// would silently analyse a made-up vessel.
export function isVesselDataComplete(settings) {
  return ["lengthM", "beamM", "maxDraftM", "grossTonnage"].every((field) =>
    Number.isFinite(Number.parseFloat(settings?.[field])),
  )
}

export const CARGO_OPTIONS = [
  {
    key: "DangerousGoods",
    label: "Dangerous goods",
    tooltip:
      "Marks if the vessel carries cargo classified as dangerous under IMO rules.",
  },
  {
    key: "RadioactiveMaterial",
    label: "Radioactive material",
    tooltip: "Specifies if the vessel carries radioactive substances.",
  },
  {
    key: "MarpolAnnexIICatX",
    label: "MARPOL Annex II Cat X",
    tooltip:
      "Cargo highly hazardous to the marine environment (MARPOL Annex II Category X).",
  },
  {
    key: "MarpolAnnexIICatY",
    label: "MARPOL Annex II Cat Y",
    tooltip:
      "Cargo presenting moderate hazards to the marine environment (MARPOL Annex II Category Y).",
  },
  {
    key: "MarpolAnnexIICatZ",
    label: "MARPOL Annex II Cat Z",
    tooltip:
      "Cargo presenting low hazards to the marine environment (MARPOL Annex II Category Z).",
  },
  {
    key: "MarpolAnnexIIOthers",
    label: "MARPOL Annex II other",
    tooltip:
      "Cargo not covered under Categories X, Y, or Z of MARPOL Annex II.",
  },
  {
    key: "MarpolAnnexIOils",
    label: "MARPOL Annex I oils",
    tooltip:
      "Marks if the vessel carries oil or oil-based products as defined by MARPOL Annex I.",
  },
]

export const LOAD_STATE_OPTIONS = [
  "Packaged",
  "LiquidBulk",
  "SolidBulk",
  "OtherCargo",
  "Ballast",
]

export const SHIP_TYPE_OPTIONS = [
  "Oil Tanker",
  "Chemical Tanker",
  "Gas Carrier",
  "LNG Carrier",
  "Other Tanker",
  "Cargo",
  "Passenger",
  "State / Navy Ship",
  "Container Ship",
  "Other",
]

// Parse + sort a consumption profile into ascending numeric rows, dropping
// non-finite entries. Accepts plain rows ([{ knots, consumption }]) or the
// request-ready profile object from /consumption-profiles ({ values: [...] }).
function parseConsumptionProfiles(consumptionProfileData) {
  const rows = Array.isArray(consumptionProfileData)
    ? consumptionProfileData
    : (consumptionProfileData?.values ?? [])
  return rows
    .map((profile) => ({
      knots: parseFloat(profile?.knots),
      consumption: parseFloat(profile?.consumption),
    }))
    .filter(
      (profile) =>
        Number.isFinite(profile.knots) && Number.isFinite(profile.consumption),
    )
    .sort((a, b) => a.knots - b.knots)
}

// Consumption rate [t/d] at a given speed, linearly interpolated between the
// profile's support points and clamped to the endpoints. Shared by
// computeTotalConsumption and the planner's per-waypoint chart.
export function consumptionRateAt(speed, consumptionProfileData) {
  const profiles = parseConsumptionProfiles(consumptionProfileData)
  if (!profiles.length) return null
  if (speed <= profiles[0].knots) return profiles[0].consumption
  const last = profiles[profiles.length - 1]
  if (speed >= last.knots) return last.consumption
  for (let i = 0; i < profiles.length - 1; i++) {
    const lower = profiles[i]
    const upper = profiles[i + 1]
    if (speed >= lower.knots && speed < upper.knots) {
      const fraction = (speed - lower.knots) / (upper.knots - lower.knots)
      return lower.consumption + fraction * (upper.consumption - lower.consumption)
    }
  }
  return null
}

// The profile row whose knots are closest to a given speed (for chip/table
// highlighting), or null if the profile is empty.
export function nearestConsumptionRow(speed, consumptionProfileData) {
  const profiles = parseConsumptionProfiles(consumptionProfileData)
  if (!profiles.length) return null
  if (!Number.isFinite(speed)) return null
  return profiles.reduce((best, row) =>
    Math.abs(row.knots - speed) < Math.abs(best.knots - speed) ? row : best,
  )
}

export const DECIMAL_REGEX = /^\d+(\.\d*)?$/

// Normalizes comma decimals and validates like the modal's field handlers:
// empty/invalid → invalid; optional minValue for strictly positive fields.
export function validateDecimal(
  raw,
  { minValue = null, maxValue = null, allowEmpty = false } = {},
) {
  const value = String(raw).replace(/,/g, ".")
  if (value === "") return { value, invalid: !allowEmpty }
  const num = parseFloat(value)
  const invalid =
    !DECIMAL_REGEX.test(value) ||
    (minValue != null && (Number.isNaN(num) || num < minValue)) ||
    (maxValue != null && num > maxValue)
  return { value, invalid }
}

// Order-independent value equality for planner settings (nested cargo flags
// included). Used to skip a needless commit — and thus a needless draft
// invalidation — when the edited draft matches the committed settings but its
// keys were inserted in a different order.
export function plannerSettingsEqual(a, b) {
  if (a === b) return true
  if (!a || !b || typeof a !== "object" || typeof b !== "object") {
    return a === b
  }
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    const va = a[key]
    const vb = b[key]
    if (
      va &&
      vb &&
      typeof va === "object" &&
      typeof vb === "object"
    ) {
      if (!plannerSettingsEqual(va, vb)) return false
    } else if (va !== vb) {
      return false
    }
  }
  return true
}

// Trims a trailing dot from edited numeric strings before committing them
// into plannerVesselSettings (same semantics as the specifications modal).
export function cleanDraftPlannerSettings(settings) {
  const clean = { ...settings }
  if (typeof clean.simplifyEpsilonM === "string") {
    const trimmed = clean.simplifyEpsilonM.endsWith(".")
      ? clean.simplifyEpsilonM.slice(0, -1)
      : clean.simplifyEpsilonM
    clean.simplifyEpsilonM = parseFloat(trimmed) || 0
  }
  return clean
}
